import { useState } from 'react';

import { IconKinds, type IconKind } from '@sb/design-system';

import { OptionsList, type OptionsListOption } from './OptionsList';
import type { OptionsPickerOption } from './types';

interface OptionsPickerCardProps {
  options: OptionsPickerOption[];
  /**
   * Fires when the user picks a row. Passes the row's `label` so the
   * caller can route the choice through `sendMessage` and have it
   * appear in the message stream as if the user had typed it.
   */
  onConfirm: (label: string) => void;
}

const DEFAULT_OPTION_ICON: IconKind = 'questionmarkCircle';
const ICON_KIND_SET: ReadonlySet<string> = new Set(IconKinds);

function toIconKind(raw: string | undefined): IconKind {
  if (raw !== undefined && ICON_KIND_SET.has(raw)) {
    return raw as IconKind;
  }

  return DEFAULT_OPTION_ICON;
}

/**
 * Multi-option follow-up card. Thin wrapper over the shared
 * `OptionsList` — adds a 14px top margin so it sits clear of the
 * preceding message bubble, then maps `OptionsPickerOption`
 * (`label`, `description`, optional `iconKind`) onto the
 * `OptionsListOption` shape the list component renders.
 */
export function OptionsPickerCard({
  options,
  onConfirm,
}: OptionsPickerCardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listOptions: OptionsListOption[] = options.map((o) => ({
    id: o.id,
    iconKind: toIconKind(o.iconKind),
    title: o.label,
    subtitle: o.description,
  }));

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const picked = options.find((o) => o.id === id);

    if (picked) onConfirm(picked.label);
  };

  return (
    <div style={{ marginTop: 14 }}>
      <OptionsList
        options={listOptions}
        selectedId={selectedId}
        onSelect={handleSelect}
      />
    </div>
  );
}
